// pages/tests/MonthlyTestForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AuthService from '../../Auth/AuthService';
import { SECTIONS, STANDARDS, SUBJECTS } from '../../utils/constants';

const steps = ['Basic Information', 'Test Configuration', 'Instructions & Files'];

const MonthlyTestForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    academicYear: '2024-2025',
    standard: '',
    section: '',
    subject: '',
    testName: '',
    testDate: null,
    maxMarks: '',
    passingMarks: '',
    instructions: '',
    status: 'DRAFT',
    files: []
  });

  const [errors, setErrors] = useState({});
  const [classes, setClasses] = useState(STANDARDS);
  const [sections, setSections] = useState(SECTIONS);
  const [subjects, setSubjects] = useState(SUBJECTS);

  // Sample data - replace with API calls
  // const sampleClasses = [
  //   { id: 1, name: 'Class 10' },
  //   { id: 2, name: 'Class 11' },
  //   { id: 3, name: 'Class 12' }
  // ];

  // const sampleSections = [
  //   { id: 1, name: 'A', classId: 1 },
  //   { id: 2, name: 'B', classId: 1 },
  //   { id: 3, name: 'A', classId: 2 },
  //   { id: 4, name: 'B', classId: 2 }
  // ];

  // const sampleSubjects = [
  //   { id: 1, name: 'Mathematics', classId: 1 },
  //   { id: 2, name: 'Science', classId: 1 },
  //   { id: 3, name: 'Physics', classId: 2 },
  //   { id: 4, name: 'Chemistry', classId: 2 }
  // ];

  useEffect(() => {
    // fetchClasses();
    // fetchSubjects();
    if (isEditMode) {
      fetchTestData();
    }
  }, [isEditMode]);

  // useEffect(() => {
  //   if (formData.class) {
  //     fetchSections(formData.class);
  //   }
  // }, [formData.class]);

  // const fetchClasses = () => {
  //   setClasses(STANDARDS);
  // };

  // const fetchSections = (classId) => {
  //   // Replace with API call
  //   const filtered = SECTIONS.filter(s => s === classId);
  //   setSections(filtered);
  // };

  // const fetchSubjects = async () => {
  //   // Replace with API call
  //   setSubjects(sampleSubjects);
  // };

  const fetchTestData = async () => {
    setLoading(true);
    try {
      // const response = await axios.get(`/api/tests/${id}`);
      // setFormData(response.data);

      // Sample data for edit mode
      setFormData({
        academicYear: '2024-2025',
        standard: '',
        section: '',
        subject: '',
        testName: 'Monthly Test - January',
        testDate: new Date('2024-01-15'),
        maxMarks: 100,
        passingMarks: 35,
        instructions: 'Bring your own calculator',
        status: 'DRAFT',
        files: []
      });
    } catch (error) {
      setError('Failed to load test data');
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.academicYear) newErrors.academicYear = 'Academic year is required';
      if (!formData.standard) newErrors.standard= 'Standard is required';
      if (!formData.section) newErrors.section = 'Section is required';
      if (!formData.subject) newErrors.subject = 'Subject is required';
      if (!formData.testName) newErrors.testName = 'Test name is required';
    }

    if (step === 1) {
      if (!formData.testDate) newErrors.testDate = 'Test date is required';
      if (!formData.maxMarks) newErrors.maxMarks = 'Maximum marks is required';
      if (!formData.passingMarks) newErrors.passingMarks = 'Passing marks is required';
      if (formData.passingMarks && formData.maxMarks &&
        parseInt(formData.passingMarks) > parseInt(formData.maxMarks)) {
        newErrors.passingMarks = 'Passing marks cannot exceed maximum marks';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      testDate: date
    });
    if (errors.testDate) {
      setErrors({
        ...errors,
        testDate: ''
      });
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setFormData({
      ...formData,
      files: [...formData.files, ...files]
    });
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...formData.files];
    newFiles.splice(index, 1);
    setFormData({
      ...formData,
      files: newFiles
    });
  };

  const handleSubmit = async (status = null) => {
    if (!validateStep(0) || !validateStep(1)) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        status: status || formData.status
      };

      if (isEditMode) {
        await AuthService.putAuth(AuthService.pathUrl("PUT", `/monthly-tests/${id}`), submitData);
      } else {
        await AuthService.postAuth(AuthService.pathUrl('POST', '/monthly-tests'), submitData);
      }

      setSuccess(`Test ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => {
        navigate('/tests');
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save test');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.academicYear}>
                <InputLabel>Academic Year *</InputLabel>
                <Select
                  value={formData.academicYear}
                  onChange={handleChange('academicYear')}
                  label="Academic Year *"
                >
                  <MenuItem value="2024-2025">2024-2025</MenuItem>
                  <MenuItem value="2023-2024">2023-2024</MenuItem>
                </Select>
                {errors.academicYear && (
                  <FormHelperText>{errors.academicYear}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} size={2}>
              <FormControl fullWidth error={!!errors.standard}>
                <InputLabel>Standard *</InputLabel>
                <Select
                  value={formData.standard}
                  onChange={handleChange('standard')}
                  label="standard *"
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      {cls}
                    </MenuItem>
                  ))}
                </Select>
                {errors.standard && (
                  <FormHelperText>{errors.standard}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} size={2}>
              <FormControl fullWidth error={!!errors.section} disabled={!formData.standard}>
                <InputLabel>Section *</InputLabel>
                <Select
                  value={formData.section}
                  onChange={handleChange('section')}
                  label="Section *"
                >
                  {sections.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </Select>
                {errors.section && (
                  <FormHelperText>{errors.section}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* <FormControl fullWidth error={!!errors.subject} disabled={!formData.section}>
                <InputLabel>Subject *</InputLabel>
                <Select
                  value={formData.subject}
                  onChange={handleChange('subject')}
                  label="Subject *"
                >
                  {subjects
                    .filter(s => !formData.class || !formData.section)
                    .map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject}
                      </MenuItem>
                    ))}
                </Select>
                {errors.subjectId && (
                  <FormHelperText>{errors.subjectId}</FormHelperText>
                )}
              </FormControl> */}
              <TextField
                fullWidth
                label="Subject Name *"
                value={formData.subject}
                onChange={handleChange('subject')}
                error={!!errors.subject}
                helperText={errors.subject}
                placeholder="e.g., English, Hindi, Sanskrit"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Test Name *"
                value={formData.testName}
                onChange={handleChange('testName')}
                error={!!errors.testName}
                helperText={errors.testName}
                placeholder="e.g., Monthly Test - January 2024"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Test Date *"
                  value={formData.testDate}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.testDate}
                      helperText={errors.testDate}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Marks *"
                value={formData.maxMarks}
                onChange={handleChange('maxMarks')}
                error={!!errors.maxMarks}
                helperText={errors.maxMarks}
                slotProps={{
                  htmlInput: { min: 0 }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} size={2}>
              <TextField
                fullWidth
                type="number"
                label="Passing Marks *"
                value={formData.passingMarks}
                onChange={handleChange('passingMarks')}
                error={!!errors.passingMarks}
                helperText={errors.passingMarks}
                slotProps={{ htmlInput: { min: 0, max: formData.maxMarks || 100 } }}
              />
            </Grid>

            <Grid item xs={12} md={6} size={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange('status')}
                  label="Status"
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="PUBLISHED">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Instructions"
                value={formData.instructions}
                onChange={handleChange('instructions')}
                placeholder="Enter any special instructions for students..."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Attachments
              </Typography>
              <Paper
                sx={{
                  p: 3,
                  border: '2px dashed',
                  borderColor: 'divider',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
                component="label"
              >
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1">
                  Click to upload files or drag and drop
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  PDF, DOC, DOCX, JPG, PNG (max 10MB each)
                </Typography>
              </Paper>

              {formData.files.length > 0 && (
                <List sx={{ mt: 2 }}>
                  {formData.files.map((file, index) => (
                    <ListItem
                      key={index}
                      divider
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFile(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(2)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Monthly Test' : 'Create Monthly Test'}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => navigate('/tests')}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>

            {activeStep === steps.length - 1 ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => handleSubmit('DRAFT')}
                  disabled={loading}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSubmit()}
                  disabled={loading}
                >
                  {isEditMode ? 'Update' : 'Create'} Test
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MonthlyTestForm;